/**
 * ExportPanel - マップエクスポートパネルコンポーネント
 *
 * マップデータをJSONフォーマットでエクスポートする機能を提供。
 * マップ情報の編集も可能。
 */

import { useState } from 'react';
import type { EditorHex } from './HexGrid';

// ============================================================================
// 型定義
// ============================================================================

export interface MapInfo {
  name: string;
  designer: string;
  players: number[];
}

export interface ExportPanelProps {
  hexes: EditorHex[];
  mapInfo: MapInfo;
  onMapInfoChange: (info: MapInfo) => void;
}

// ============================================================================
// ヘルパー関数
// ============================================================================

/**
 * EditorHex配列をマップスキーマ形式のJSONに変換
 */
function convertToMapSchema(hexes: EditorHex[], mapInfo: MapInfo): object {
  // 都市を抽出
  const cities = hexes
    .filter((hex) => hex.city)
    .map((hex) => ({
      name: hex.city!.name,
      color: hex.city!.colors,  // 配列形式
      coordinates: { q: hex.q, r: hex.r },
      dice_color: hex.city!.diceColors.length === 1 ? hex.city!.diceColors[0] : hex.city!.diceColors,
      dice_numbers: hex.city!.diceNumbers,
    }));

  // 町を抽出
  const towns = hexes
    .filter((hex) => hex.town && !hex.city)
    .map((hex) => ({
      name: hex.town!.name,
      coordinates: { q: hex.q, r: hex.r },
      urbanizable: hex.town!.urbanizable,
      terrain: hex.terrain === 'plains' ? 'plain' : hex.terrain,
    }));

  // 地形を抽出（都市・町以外）
  const mountains = hexes
    .filter((hex) => hex.terrain === 'mountains' && !hex.city && !hex.town)
    .map((hex) => ({ q: hex.q, r: hex.r }));

  const rivers = hexes
    .filter((hex) => hex.terrain === 'river' && !hex.city && !hex.town)
    .map((hex) => ({ q: hex.q, r: hex.r }));

  const plains = hexes
    .filter((hex) => hex.terrain === 'plains' && !hex.city && !hex.town)
    .map((hex) => ({ q: hex.q, r: hex.r }));

  // 壁を抽出
  const walls = hexes
    .filter((hex) => hex.blockedEdges && hex.blockedEdges.length > 0)
    .map((hex) => ({
      coordinates: { q: hex.q, r: hex.r },
      blocked_edges: hex.blockedEdges!,
    }));

  // 敷設済み線路を抽出
  const preLaidTracks = hexes
    .filter((hex) => hex.preLaidTrack)
    .map((hex) => ({
      coordinates: { q: hex.q, r: hex.r },
      tile_type: hex.preLaidTrack!.tileType,
      rotation: hex.preLaidTrack!.rotation,
    }));

  const result: Record<string, unknown> = {
    map_info: {
      name: mapInfo.name,
      designer: mapInfo.designer,
      players: mapInfo.players,
      coordinate_system: 'axial',
      origin: { q: 0, r: 0 },
      orientation: 'pointy_top',
    },
    cities,
    towns,
    terrain: {
      mountains,
      rivers,
      plains,
    },
  };

  // 壁がある場合のみ含める
  if (walls.length > 0) {
    result.walls = walls;
  }

  // 敷設済み線路がある場合のみ含める
  if (preLaidTracks.length > 0) {
    result.pre_laid_tracks = preLaidTracks;
  }

  return result;
}

// ============================================================================
// ExportPanel コンポーネント
// ============================================================================

export function ExportPanel({
  hexes,
  mapInfo,
  onMapInfoChange,
}: ExportPanelProps) {
  const [showPreview, setShowPreview] = useState(false);
  const [playerInput, setPlayerInput] = useState(mapInfo.players.join(', '));

  const handlePlayersChange = (value: string) => {
    setPlayerInput(value);
    const players = value
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n) && n > 0);
    if (players.length > 0) {
      onMapInfoChange({ ...mapInfo, players });
    }
  };

  const exportData = convertToMapSchema(hexes, mapInfo);
  const jsonString = JSON.stringify(exportData, null, 2);

  const handleDownload = () => {
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mapInfo.name.toLowerCase().replace(/\s+/g, '_')}_map_data.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      alert('Copied to clipboard!');
    } catch {
      alert('Failed to copy to clipboard');
    }
  };

  // 統計情報を計算
  const stats = {
    totalHexes: hexes.length,
    cities: hexes.filter((h) => h.city).length,
    towns: hexes.filter((h) => h.town && !h.city).length,
    mountains: hexes.filter((h) => h.terrain === 'mountains').length,
    rivers: hexes.filter((h) => h.terrain === 'river').length,
    plains: hexes.filter((h) => h.terrain === 'plains').length,
    walls: hexes.filter((h) => h.blockedEdges && h.blockedEdges.length > 0).length,
    preLaidTracks: hexes.filter((h) => h.preLaidTrack).length,
  };

  return (
    <div className="flex flex-col gap-4 p-4 bg-card border border-border rounded-lg">
      <h3 className="text-lg font-semibold">Map Information</h3>

      {/* Map Info Form */}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium mb-1">Map Name</label>
          <input
            type="text"
            value={mapInfo.name}
            onChange={(e) => onMapInfoChange({ ...mapInfo, name: e.target.value })}
            placeholder="e.g., Barbados"
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Designer</label>
          <input
            type="text"
            value={mapInfo.designer}
            onChange={(e) => onMapInfoChange({ ...mapInfo, designer: e.target.value })}
            placeholder="e.g., Your Name"
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Player Counts (comma-separated)</label>
          <input
            type="text"
            value={playerInput}
            onChange={(e) => handlePlayersChange(e.target.value)}
            placeholder="e.g., 1, 2, 3, 4"
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm"
          />
        </div>
      </div>

      {/* Statistics */}
      <div className="border-t border-border pt-3">
        <h4 className="text-sm font-semibold mb-2 text-muted-foreground">Statistics</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>Total Hexes: <span className="font-mono">{stats.totalHexes}</span></div>
          <div>Cities: <span className="font-mono">{stats.cities}</span></div>
          <div>Towns: <span className="font-mono">{stats.towns}</span></div>
          <div>Mountains: <span className="font-mono">{stats.mountains}</span></div>
          <div>Rivers: <span className="font-mono">{stats.rivers}</span></div>
          <div>Plains: <span className="font-mono">{stats.plains}</span></div>
          <div>Walls: <span className="font-mono">{stats.walls}</span></div>
          <div>Pre-laid Tracks: <span className="font-mono">{stats.preLaidTracks}</span></div>
        </div>
      </div>

      {/* Export Actions */}
      <div className="border-t border-border pt-3 space-y-2">
        <h4 className="text-sm font-semibold mb-2 text-muted-foreground">Export</h4>
        <div className="flex gap-2">
          <button
            className="flex-1 px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors"
            onClick={handleDownload}
          >
            Download JSON
          </button>
          <button
            className="flex-1 px-4 py-2 text-sm bg-muted hover:bg-muted/80 rounded-md transition-colors"
            onClick={handleCopyToClipboard}
          >
            Copy to Clipboard
          </button>
        </div>
        <button
          className="w-full px-4 py-2 text-sm bg-muted hover:bg-muted/80 rounded-md transition-colors"
          onClick={() => setShowPreview(!showPreview)}
        >
          {showPreview ? 'Hide' : 'Show'} JSON Preview
        </button>
      </div>

      {/* JSON Preview */}
      {showPreview && (
        <div className="border-t border-border pt-3">
          <pre className="bg-muted p-3 rounded-md text-xs overflow-auto max-h-[300px] font-mono">
            {jsonString}
          </pre>
        </div>
      )}
    </div>
  );
}

export default ExportPanel;
